import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import type { AuthSessionResponse, WorkspaceInvite } from "@yagni/shared";
import { GoogleBrandButton } from "../components/GoogleBrand.js";
import { Badge } from "../ui/badge.js";
import { Button } from "../ui/button.js";
import { Card } from "../ui/card.js";
import { AUTHENTICATED_DEFAULT_PATH } from "../routes/defaults.js";
import { captureUiError } from "../sentry.js";

interface InviteLookupResponse {
  invite: WorkspaceInvite;
  workspaceName: string;
  /**
   * The invite is already used and the signed-in person is a member through
   * it. Google sign-in joins a person with a pending invite before sending
   * them back here, so this is the usual return from sign-in (YAG-1065).
   */
  alreadyMember?: boolean;
  workspaceId?: string;
}

interface AcceptInviteResponse {
  accepted?: boolean;
  alreadyMember?: boolean;
  workspaceId: string;
}

interface InviteLookupError {
  error?: string;
  /** Set on a 410: `invite_accepted`, `invite_revoked` or `invite_expired`. */
  code?: string;
}

/**
 * Marks the return from a sign-in started on this page. Starting sign-in here
 * is the person asking to join, so on the way back a pending invite for the
 * signed-in email is accepted without a second click.
 */
const SIGNED_IN_HERE_PARAM = "accept";

function buildGoogleAuthUrl(returnTo: string): string {
  const params = new URLSearchParams({ returnTo });
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (timezone) params.set("timezone", timezone);
  return `/api/auth/google?${params.toString()}`;
}

const SWITCH_FAILED = "Invite accepted, but failed to switch workspaces";

/**
 * Open the invited workspace: switch the session to it unless the session is
 * known to be on it already (an unread session is switched, since it may be
 * on another workspace), then go home. Returns the reason when the switch is
 * refused or does not answer; never throws.
 */
async function enterWorkspace(
  workspaceId: string,
  currentWorkspaceId: string | null,
): Promise<string | null> {
  if (currentWorkspaceId !== workspaceId) {
    try {
      const switchRes = await fetch("/api/auth/switch-workspace", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ workspaceId }),
      });

      if (!switchRes.ok) {
        const switchBody = (await switchRes.json().catch(() => ({}))) as { error?: string };
        return switchBody.error ?? SWITCH_FAILED;
      }
    } catch (err) {
      captureUiError(err, {
        area: "Auth",
        operation: "invite_switch_workspace",
        route: "/invite",
        workspaceId,
        severity: "error",
        expected: false,
      });
      return SWITCH_FAILED;
    }
  }

  window.location.assign(AUTHENTICATED_DEFAULT_PATH);
  return null;
}

/**
 * Accept the invite and open its workspace. A 401 sends the person to sign in
 * and back. Returns the reason it could not, or null when it is on its way.
 */
async function acceptAndEnter(
  token: string,
  currentWorkspaceId: string | null,
  googleAuthUrl: string,
): Promise<string | null> {
  try {
    const res = await fetch(`/api/invites/${token}/accept`, { method: "POST" });
    if (res.status === 401) {
      window.location.assign(googleAuthUrl);
      return null;
    }

    const body = (await res.json().catch(() => ({}))) as AcceptInviteResponse & {
      error?: string;
    };

    if (!res.ok) return body.error ?? "Failed to accept invite";

    return await enterWorkspace(body.workspaceId, currentWorkspaceId);
  } catch (err) {
    captureUiError(err, {
      area: "Auth",
      operation: "invite_accept",
      route: "/invite",
      severity: "error",
      expected: false,
    });
    return "Failed to accept invite";
  }
}

function acceptButtonLabel(alreadyMember: boolean, pending: boolean): string {
  if (alreadyMember) return "Open workspace";
  if (pending) return "Accepting invite...";
  return "Accept invite";
}

export function InvitePage() {
  const { token } = useParams<{ token: string }>();
  const [inviteData, setInviteData] = useState<InviteLookupResponse | null>(null);
  const [session, setSession] = useState<AuthSessionResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [acceptPending, setAcceptPending] = useState(false);
  // What the page says while it takes the person in; null shows the invite.
  const [entering, setEntering] = useState<string | null>(null);
  // A used invite seen signed out: its invitee may be coming back to it, so
  // the page asks them to sign in instead of stopping at the message.
  const [signInToOpen, setSignInToOpen] = useState(false);
  const signedInHere = new URLSearchParams(window.location.search).has(SIGNED_IN_HERE_PARAM);
  const returnTo = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    params.set(SIGNED_IN_HERE_PARAM, "1");
    return `${window.location.pathname}?${params.toString()}`;
  }, []);
  const googleAuthUrl = useMemo(() => buildGoogleAuthUrl(returnTo), [returnTo]);

  const loadInvite = useCallback(async () => {
    if (!token) {
      setError("Invite token is missing");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    setActionError(null);
    setSignInToOpen(false);

    try {
      const [inviteRes, sessionRes] = await Promise.all([
        fetch(`/api/invites/${token}`),
        fetch("/api/auth/session"),
      ]);

      let invitePayload: InviteLookupResponse | null = null;
      let lookupError: InviteLookupError | null = null;
      if (inviteRes.ok) {
        invitePayload = (await inviteRes.json()) as InviteLookupResponse;
        setInviteData(invitePayload);
      } else {
        lookupError = (await inviteRes.json().catch(() => ({}))) as InviteLookupError;
        setInviteData(null);
        setError(lookupError.error ?? "Invite not found");
      }

      let sessionPayload: AuthSessionResponse | null = null;
      if (sessionRes.ok) {
        sessionPayload = (await sessionRes.json()) as AuthSessionResponse;
        setSession(sessionPayload);
      } else {
        setSession(null);
      }

      // Only a session that answered "not signed in" is asked to sign in; a
      // session read that failed otherwise keeps the plain message.
      if (lookupError?.code === "invite_accepted" && sessionRes.status === 401) {
        setSignInToOpen(true);
      }

      const currentWorkspaceId = sessionPayload?.workspace.id ?? null;
      const signedInAsInvitee =
        sessionPayload !== null &&
        invitePayload !== null &&
        sessionPayload.user.email.toLowerCase() === invitePayload.invite.email.toLowerCase();

      // Show what is happening, run the step, and on a refusal show the
      // invite again with the reason.
      const takeIn = async (status: string, step: () => Promise<string | null>) => {
        setEntering(status);
        const reason = await step();
        if (reason) {
          setEntering(null);
          setActionError(reason);
        }
      };

      if (invitePayload?.alreadyMember && invitePayload.workspaceId) {
        const workspaceId = invitePayload.workspaceId;
        await takeIn(`You've joined ${invitePayload.workspaceName}. Taking you there now.`, () =>
          enterWorkspace(workspaceId, currentWorkspaceId),
        );
      } else if (invitePayload && signedInAsInvitee && signedInHere) {
        // Back from a sign-in started here, as an account that already had a
        // workspace, so sign-in left the invite pending: accept it now.
        await takeIn(`Joining ${invitePayload.workspaceName}...`, () =>
          acceptAndEnter(token, currentWorkspaceId, googleAuthUrl),
        );
      }
    } catch {
      setInviteData(null);
      setSession(null);
      setError("Failed to load invite");
    } finally {
      setLoading(false);
    }
  }, [googleAuthUrl, signedInHere, token]);

  useEffect(() => {
    loadInvite().catch(console.error);
  }, [loadInvite]);

  const handleAccept = useCallback(async () => {
    if (!token) {
      return;
    }

    setAcceptPending(true);
    setActionError(null);

    const acceptError = await acceptAndEnter(token, session?.workspace.id ?? null, googleAuthUrl);
    if (acceptError) setActionError(acceptError);
    setAcceptPending(false);
  }, [googleAuthUrl, session, token]);

  const handleSignOutAndContinue = useCallback(async () => {
    await fetch("/api/auth/sign-out", { method: "POST" }).catch(() => undefined);
    window.location.assign(googleAuthUrl);
  }, [googleAuthUrl]);

  const signedInEmail = session?.user.email.toLowerCase() ?? null;
  const invitedEmail = inviteData?.invite.email.toLowerCase() ?? null;
  const wrongAccount =
    signedInEmail !== null && invitedEmail !== null && signedInEmail !== invitedEmail;

  return (
    <div className="min-h-screen [background:var(--canvas-field)] flex items-center justify-center px-4 py-8">
      {/* Glass: the invite is staged, waiting on the human to accept. */}
      <Card variant="staged" padding="none" className="w-full max-w-lg">
        <div className="border-b border-[var(--glass-divider)] px-6 py-5">
          <h1 className="text-2xl font-semibold text-[var(--text-primary)]">Workspace invite</h1>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">
            Accept your invitation and join the team.
          </p>
        </div>

        <div className="space-y-4 px-6 py-5">
          {loading ? (
            <div className="text-sm text-[var(--text-secondary)]">Loading invite...</div>
          ) : null}

          {!loading && error ? (
            <Card tone="error" padding="sm" className="rounded-xl text-sm text-error">
              {error}
            </Card>
          ) : null}

          {!loading && signInToOpen ? (
            <>
              <p className="text-sm text-[var(--text-secondary)]">
                If this invite was sent to you, sign in to open the workspace.
              </p>
              <GoogleBrandButton href={googleAuthUrl} />
            </>
          ) : null}

          {!loading && inviteData && entering ? (
            <div role="status" className="text-sm text-[var(--text-secondary)]">
              {entering}
            </div>
          ) : null}

          {!loading && inviteData && !entering ? (
            <>
              <Card variant="outlined" padding="md" className="rounded-xl">
                <div className="text-sm text-[var(--text-secondary)]">Workspace</div>
                <div className="mt-1 text-lg font-medium text-[var(--text-primary)]">
                  {inviteData.workspaceName}
                </div>
                <div className="mt-3 text-sm text-[var(--text-secondary)]">Invited email</div>
                <div className="mt-1 text-sm font-medium text-[var(--text-primary)]">
                  {inviteData.invite.email}
                </div>
                <div className="mt-3 text-sm text-[var(--text-secondary)]">Role</div>
                <Badge
                  size="xs"
                  className="mt-1 border border-[var(--accent)] bg-[var(--accent-muted)] text-[var(--accent)] px-2.5 py-1"
                >
                  {inviteData.invite.role}
                </Badge>
              </Card>

              {session ? (
                <Card variant="outlined" padding="sm" className="rounded-xl px-4 py-3">
                  <div className="text-sm text-[var(--text-secondary)]">Signed in as</div>
                  <div className="mt-1 text-sm font-medium text-[var(--text-primary)]">
                    {session.user.email}
                  </div>
                </Card>
              ) : null}

              {actionError ? (
                <Card tone="error" padding="sm" className="rounded-xl text-sm text-error">
                  {actionError}
                </Card>
              ) : null}

              {wrongAccount ? (
                <>
                  <Card tone="warning" padding="sm" className="rounded-xl text-sm text-warning">
                    This invite was sent to {inviteData.invite.email}. Sign in with that account to
                    continue.
                  </Card>
                  <GoogleBrandButton onClick={() => void handleSignOutAndContinue()} />
                </>
              ) : session ? (
                <Button
                  type="button"
                  onClick={() => void handleAccept()}
                  disabled={acceptPending}
                  loading={acceptPending}
                  className="w-full rounded-xl h-auto py-3"
                >
                  {acceptButtonLabel(inviteData.alreadyMember === true, acceptPending)}
                </Button>
              ) : (
                <GoogleBrandButton href={googleAuthUrl} />
              )}
            </>
          ) : null}
        </div>
      </Card>
    </div>
  );
}
